/* eslint-disable @typescript-eslint/no-non-null-assertion */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import { ButtonIcon, Table } from '../../../../components';
import React, { useState } from 'react';
import { useI18n } from '@whizz/react-i18n';
import { useQuery } from 'react-query';
import { GLOBAL_ICONS, QUERIES_IDS } from '../../../../util/vars';
import { repository } from '@whizz/api-client';
import type { WeeklyLeadersEmail } from '@whizz/api-model';
import { Checkbox } from '../../../RosterManagement/pages/Students/modules/StudentsTable.components';
import Button from '../../../../components/button/Button';
import ChangePausedUntilModal from './ChangePausedUntilModal';
import { getNextWeekday } from '../../../../util/dates';
import Input from '../../../../components/input/Input';
import ChoiceGroup from '../../../../components/formGroup/ChoiceGroup';
import Loader from '../../../../components/loader/Loader';
import { DateTime } from 'luxon';

export default function EmailsTable({ onClickEdit }: Props) {
  const { translate: t } = useI18n();

  const [ search, setSearch ] = useState('');
  const [ selectedSchools, setSelectedSchools ] = useState<number[]>([]);
  const [ modalsData, setModalsData ] = useState<ModalsData>({ action: null });

  const getWeeklyLeadersEmails = useQuery({
    queryKey: QUERIES_IDS.ADMIN.GET_WEEKLY_LEADERS_EMAILS,
    queryFn: () => repository.AdminRepository.getWeeklyLeadersEmails(),
  });

  function filteredSchools() {
    const weeklyLeadersEmails = getWeeklyLeadersEmails.data?.data ?? [];

    if (null == search || '' === search) {
      return weeklyLeadersEmails;
    }

    const lowercasedSearch = search.toLowerCase();

    return weeklyLeadersEmails
      .filter((school) => {
        const foundInName = school.school.name.toLowerCase().includes(lowercasedSearch);
        const foundInPostcode = school.school.postcode?.toLowerCase().includes(lowercasedSearch);

        return foundInName || foundInPostcode;
      })
    ;
  }

  function modals() {
    if (null === modalsData.action) {
      return null;
    }

    return (
      <ChangePausedUntilModal
        toggle={() => setModalsData({ action: null })}
        schools={modalsData.schools}
        onSuccess={() => setSelectedSchools([])}
      />
    );
  }

  if (getWeeklyLeadersEmails.isLoading) {
    return (
      <Loader />
    );
  }

  return (
    <>
      {modals()}

      <div className={'flex flex-col gap-5'}>
        <div>
          <Input
            id={'search'}
            label={'Search'}
            hideLabel
            placeholder={'Search school, town or postcode'}
            onChange={(event) => setSearch(event.target.value)}
            value={search}
          />
        </div>

        <div>
          <Table
            id={'weekly_leaders_emails'}
            cols={[
              {
                id: 'edit',
                Header: t('pages.ADMIN.TABLE.HEADERS.EDIT')!,
                accessor: 'school.id',
                Cell: (props) => (
                  <ButtonIcon
                    icon={GLOBAL_ICONS.edit}
                    size={'sm'}
                    isOutline
                    isCircle

                    onClick={() => onClickEdit(props.row.original)}
                  />
                ),
                disableSortBy: true,
              },
              {
                id: 'id',
                Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_ID')!,
                accessor: 'school.id',
              },
              {
                Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_NAME')!,
                accessor: 'school.name',
              },
              {
                Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_TOWN')!,
                accessor: 'school.city',
              },
              {
                Header: t('pages.ADMIN.TABLE.HEADERS.SCHOOL_POSTCODE')!,
                accessor: 'school.postcode',
              },
              {
                id: 'next_send_date',
                Header: t('pages.ADMIN.TABLE.HEADERS.NEXT_SEND_DATE')!,
                accessor: (original) => {
                  if (!original.isEnabled) {
                    return null;
                  }

                  const now = DateTime.now().setZone(original.school.timezone);

                  let dateFrom = now;

                  if (null != original.pausedUntil) {
                    const pausedUntil = DateTime.fromISO(original.pausedUntil);

                    if (now < pausedUntil) {
                      dateFrom = pausedUntil;
                    }
                  }

                  const nextWeekday = getNextWeekday(original.weekday, original.hour, dateFrom);

                  return nextWeekday.toLocaleString();
                },
                Cell: ({ cell }) => {
                  if (null === cell.value) {
                    return (
                      <span className={'text-muted italic'}>
                        Disabled
                      </span>
                    );
                  }

                  return cell.value;
                },
              },
              {
                Header: () => (
                  <Button
                    isOutline
                    onClick={() => setModalsData({
                      action: ModalsAction.changeNextSendData,
                      schools: selectedSchools,
                    })}

                    disabled={0 === selectedSchools.length}
                  >
                    {t('pages.ADMIN.TABLE.HEADERS.CHANGE_NEXT_SEND_DATE')}
                  </Button>
                ),
                id: 'change_next_send_date',
                accessor: 'id',
                Cell: ({ cell, row }) => {
                  const original = row.original;
                  const schoolId = original.school.id;

                  return (
                    <ChoiceGroup
                      id={`change_all_none_${schoolId}`}
                      label={''}
                      hideLabel
                      checked={selectedSchools.includes(schoolId)}
                      onChange={() => setSelectedSchools((prev) => {
                        if (prev.includes(schoolId)) {
                          return prev.filter((id) => id !== schoolId);
                        }

                        return [ ...prev, schoolId ];
                      })}
                      disabled={null === cell.value || !original.isEnabled}
                    />
                  );
                },
                disableSortBy: true,
              },
            ]}
            content={filteredSchools()}
            noDataMessage={t('pages.ADMIN.TABLE.NO_DATA')}
            initialState={{
              pageSize: 50,
            }}
          />
        </div>
      </div>
    </>
  );
}


interface Props {
  onClickEdit: (weeklyLeadersEmail: WeeklyLeadersEmail) => void;
}

enum ModalsAction {
  changeNextSendData,
}

type ModalsData = {
  action: ModalsAction.changeNextSendData;
  schools: number[];
} | { action: null };
